import { useMemo, useState } from 'react';

import { useCachedData } from 'Components/Context/CachedDataContext';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { HeaderData, SortDirection, SortableTableHeader, SortableTableHeaderProps } from 'Components/Table/SortableTableHeader/SortableTableHeader';
import { Table, TableBody } from 'Components/Table/Table/Table';
import { TPRM_ROLES } from 'Helpers/UserUtils';
import { FinalizationState, ServiceAssessmentState, ServiceForNonTprmUser, ThirdPartyContact, ThirdPartyResponseWithServices, ThirdPartyService, ThirdPartyWithServicesForNonTprmUser } from 'Models/TPRM';
import { GroupOptionType, GroupedOptions } from 'Models/Types/GlobalType';

import styles from './ThirdPartiesAndServicesTable.module.css';
import { UserTypeAndThirdParties } from '../../ThirdPartiesAndServicesListing';
import { ThirdPartiesAndServicesTableRow, ThirdPartiesAndServicesTableRowData } from '../ThirdPartiesAndServicesTableRow/ThirdPartiesAndServicesTableRow';

enum SortOption {
    THIRD_PARTY_NAME = 'THIRD_PARTY_NAME',
    SERVICE_NAME = 'SERVICE_NAME',
    SERVICE_PROGRESS = 'SERVICE_PROGRESS',
    INHERENT_RISK_SCORE = 'INHERENT_RISK_SCORE',
    RESIDUAL_RISK_SCORE = 'RESIDUAL_RISK_SCORE',
    ASSESSMENT_DUE_DATE = 'ASSESSMENT_DUE_DATE',
}

export interface ThirdPartiesAndServicesTableProps {
    onDisplayContacts: (contacts: ThirdPartyContact[], header: string) => void;
    onDisplayUsers: (userIds: string[], header: string) => void;
    onConfirmFinalizeService: (thirdParty: ThirdPartyResponseWithServices, service: ThirdPartyService) => void;
    onConfirmFinalizeThirdParty: (thirdParty: ThirdPartyResponseWithServices) => void;
    onConfirmSubmitServiceToBeFinalized: (thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser, service: ThirdPartyService | ServiceForNonTprmUser) => void;
    onConfirmSubmitThirdPartyToBeFinalized: (thirdParty: ThirdPartyResponseWithServices | ThirdPartyWithServicesForNonTprmUser) => void;
    onSetAssessmentDueDate: (service: ThirdPartyService) => void;
    onSetIrqTargetCompletionDate: (service: ThirdPartyService) => void;
    userTypeAndThirdParties: UserTypeAndThirdParties;
}

const getCompletedStepNumberForService = (thirdParty: ThirdPartyResponseWithServices, service: ThirdPartyService) => {
    if (service.assessment_workflow_data.ddq_completed_time && service.assessment_workflow_data.state === ServiceAssessmentState.PERFORMING_DUE_DILIGENCE) {
        return 5;
    } else if (service.assessment_workflow_data.state === ServiceAssessmentState.PERFORMING_DUE_DILIGENCE) {
        return 4;
    } else if (service.assessment_workflow_data.state !== ServiceAssessmentState.AWAITING_ASSESSMENT) {
        return 3;
    } else if (service.finalization_state === FinalizationState.FINALIZED) {
        return 2;
    } else if (thirdParty.finalization_state === FinalizationState.FINALIZED) {
        return 1;
    } else {
        return 0;
    }
};

const getCompletedStepNumberForServiceForNonTprmUser = (thirdParty: ThirdPartyWithServicesForNonTprmUser, service: ServiceForNonTprmUser) => {
    if (service.assessment_workflow_data.ddq_has_been_marked_complete_by_third_party && service.assessment_workflow_data.state === ServiceAssessmentState.PERFORMING_DUE_DILIGENCE) {
        return 7;
    } else if (service.assessment_workflow_data.state === ServiceAssessmentState.PERFORMING_DUE_DILIGENCE) {
        return 6;
    } else if (service.assessment_workflow_data.state !== ServiceAssessmentState.AWAITING_ASSESSMENT) {
        return 5;
    } else if (service.finalization_state === FinalizationState.FINALIZED) {
        return 4;
    } else if (thirdParty.finalization_state === FinalizationState.FINALIZED) {
        return 3;
    } else if (service.finalization_state === FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED) {
        return 2;
    } else if (thirdParty.finalization_state === FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED) {
        return 1;
    } else {
        return 0;
    }
};

export const ThirdPartiesAndServicesTable = (props: ThirdPartiesAndServicesTableProps) => {
    const cachedData = useCachedData();

    const [currentPropertyByWhichToSort, setCurrentPropertyByWhichToSort] = useState(SortOption.THIRD_PARTY_NAME);
    const [currentSortDirection, setCurrentSortDirection] = useState(SortDirection.ASC);

    const [selectedCreatorIdsByWhichToFilter, setSelectedCreatorIdsByWhichToFilter] = useState<string[]>(props.userTypeAndThirdParties.userHasFullTprmAccess ? [] : [props.userTypeAndThirdParties.userId]);
    const [selectedManagerIdsByWhichToFilter, setSelectedManagerIdsByWhichToFilter] = useState<string[]>([]);
    const [selectedStatusesByWhichToFilter, setSelectedStatusesByWhichToFilter] = useState<FinalizationState[]>([]);
    const [selectedTagIdsByWhichToFilter, setSelectedTagIdsByWhichToFilter] = useState<string[]>([]);

    const dataForRows: ThirdPartiesAndServicesTableRowData[] = useMemo(() => {
        const userId = props.userTypeAndThirdParties.userId;
        const _dataForRows: ThirdPartiesAndServicesTableRowData[] = [];

        props.userTypeAndThirdParties.thirdParties.forEach((thirdParty) => {
            if (thirdParty.services.length === 0) {
                if (props.userTypeAndThirdParties.userHasFullTprmAccess) {
                    _dataForRows.push({ userHasTprmRole: true, userId, thirdParty: thirdParty as ThirdPartyResponseWithServices });
                } else {
                    _dataForRows.push({ userHasTprmRole: false, userId, thirdParty: thirdParty as ThirdPartyWithServicesForNonTprmUser });
                }
            } else {
                thirdParty.services.forEach((service) => {
                    if (props.userTypeAndThirdParties.userHasFullTprmAccess) {
                        const _thirdParty = thirdParty as ThirdPartyResponseWithServices;
                        const _service = service as ThirdPartyService;
                        const serviceData = {
                            service: _service,
                            serviceCompletedStepNumber: getCompletedStepNumberForService(_thirdParty, _service),
                        };
                        _dataForRows.push({ userHasTprmRole: true, userId, thirdParty: _thirdParty, serviceData });
                    } else {
                        const _thirdParty = thirdParty as ThirdPartyWithServicesForNonTprmUser;
                        const _service = service as ServiceForNonTprmUser;
                        const serviceData = {
                            service: _service,
                            serviceCompletedStepNumber: getCompletedStepNumberForServiceForNonTprmUser(_thirdParty, _service),
                        };
                        _dataForRows.push({ userHasTprmRole: false, userId, thirdParty: _thirdParty, serviceData });
                    }
                });
            }
        });

        /**
         * A helper function for comparing the service progress of two rows. Defined here for greater readability below.
         */
        const getNumericalValueForServiceProgress = (rowData: ThirdPartiesAndServicesTableRowData): number => {
            if (!rowData.serviceData) {
                // If a row does not have a service, sort by the third party's progress. This isn't strictly required to order the rows only within the context of a single column, and it goes above and beyond what we normally do with sorting by a column, but it is a nice-to-have.
                switch (rowData.thirdParty.finalization_state) {
                    case FinalizationState.DRAFT_IN_PROGRESS:
                        return -3;
                    case FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED:
                        return -2;
                    case FinalizationState.FINALIZED:
                        return -1;
                }
            } else if (rowData.serviceData.service.last_assessed && rowData.serviceData.service.assessment_workflow_data.state === ServiceAssessmentState.AWAITING_ASSESSMENT) {
                // If the row is for a service with a completed assessment, the row should drop towards the bottom of the table. Similarly to the above case where we go above and beyond, here we sort secondarily by whether the service has an assessment due date.
                return rowData.serviceData.service.assessment_workflow_setup.due_date ? 100 : 99;
            } else {
                // For services in the middle of assessments, we simply sort by the progress on the assessment as represented by the number of checkmarks in the progress cell's hover.
                return rowData.serviceData.serviceCompletedStepNumber;
            }
        };

        _dataForRows.sort((a, b) => {
            const sortResult: number = (() => {
                switch (currentPropertyByWhichToSort) {
                    case SortOption.THIRD_PARTY_NAME:
                        return a.thirdParty.name.localeCompare(b.thirdParty.name);
                    case SortOption.SERVICE_NAME:
                        return (a.serviceData?.service.name ?? '').localeCompare(b.serviceData?.service.name ?? '');
                    case SortOption.SERVICE_PROGRESS:
                        return getNumericalValueForServiceProgress(a) - getNumericalValueForServiceProgress(b);
                    case SortOption.INHERENT_RISK_SCORE:
                        const aValueInherentRisk = a.serviceData === undefined ? -1 : (a.serviceData.service.inherent_risk_score ?? 0);
                        const bValueInherentRisk = b.serviceData === undefined ? -1 : (b.serviceData.service.inherent_risk_score ?? 0);
                        return aValueInherentRisk - bValueInherentRisk;
                    case SortOption.RESIDUAL_RISK_SCORE:
                        const aValueResidualRisk = a.serviceData === undefined ? -1 : (a.serviceData.service.residual_risk_score ?? 0);
                        const bValueResidualRisk = b.serviceData === undefined ? -1 : (b.serviceData.service.residual_risk_score ?? 0);
                        return aValueResidualRisk - bValueResidualRisk;
                    case SortOption.ASSESSMENT_DUE_DATE:
                        return (a.serviceData?.service.assessment_workflow_setup.due_date ?? '').localeCompare(b.serviceData?.service.assessment_workflow_setup.due_date ?? '');
                }
            })();

            return currentSortDirection === SortDirection.ASC ? sortResult : -sortResult;
        });

        return _dataForRows.filter((row) => {
            const thirdParty = row.thirdParty;
            const service = row.serviceData?.service;

            const thirdPartyMatchesCreatorFilter = selectedCreatorIdsByWhichToFilter.length === 0 || (thirdParty.created_by && selectedCreatorIdsByWhichToFilter.includes(thirdParty.created_by));
            const thirdPartyMatchesManagerFilter = selectedManagerIdsByWhichToFilter.length === 0 || selectedManagerIdsByWhichToFilter.includes(thirdParty.vendor_manager_user_id);
            const thirdPartyMatchesStatusFilter = selectedStatusesByWhichToFilter.length === 0 || selectedStatusesByWhichToFilter.includes(thirdParty.finalization_state);
            const thirdPartyMatchesTagsFilter = selectedTagIdsByWhichToFilter.length === 0 || selectedTagIdsByWhichToFilter.filter((tagId) => thirdParty.tags.includes(tagId)).length > 0;

            const serviceMatchesCreatorFilter = service && service.created_by && (selectedCreatorIdsByWhichToFilter.length === 0 || selectedCreatorIdsByWhichToFilter.includes(service.created_by));
            const serviceMatchesManagerFilter = service && (selectedManagerIdsByWhichToFilter.length === 0 || selectedManagerIdsByWhichToFilter.includes(service.vendor_service_manager_user_id));
            const serviceMatchesStatusFilter = service && (selectedStatusesByWhichToFilter.length === 0 || selectedStatusesByWhichToFilter.includes(service.finalization_state));
            const serviceMatchesTagsFilter = service && (selectedTagIdsByWhichToFilter.length === 0 || selectedTagIdsByWhichToFilter.filter((tagId) => service.tags.includes(tagId)).length > 0);

            return (thirdPartyMatchesCreatorFilter && thirdPartyMatchesManagerFilter && thirdPartyMatchesStatusFilter && thirdPartyMatchesTagsFilter) || (serviceMatchesCreatorFilter && serviceMatchesManagerFilter && serviceMatchesStatusFilter && serviceMatchesTagsFilter);
        });
    }, [currentPropertyByWhichToSort, currentSortDirection, props.userTypeAndThirdParties.thirdParties, props.userTypeAndThirdParties.userId, props.userTypeAndThirdParties.userHasFullTprmAccess, selectedCreatorIdsByWhichToFilter, selectedManagerIdsByWhichToFilter, selectedStatusesByWhichToFilter, selectedTagIdsByWhichToFilter]);

    const statusFilterOptions: GroupedOptions[] = [
        {
            label: 'Status',
            options: Object.values(FinalizationState).map((state) => ({
                groupId: 'Status',
                label: state,
                value: state,
            })),
        },
    ];

    const headerValues: HeaderData[] = [
        { dataKey: SortOption.THIRD_PARTY_NAME, label: 'THIRD PARTY' },
        { dataKey: SortOption.SERVICE_NAME, label: 'SERVICE' },
        { dataKey: SortOption.SERVICE_PROGRESS, label: 'ASSESSMENT PROGRESS' },
        { dataKey: SortOption.INHERENT_RISK_SCORE, label: 'INHERENT RISK' },
        { dataKey: SortOption.RESIDUAL_RISK_SCORE, label: 'RESIDUAL RISK' },
        { dataKey: SortOption.ASSESSMENT_DUE_DATE, label: 'ASSESSMENT DUE DATE' },
    ];

    const sortableTableProps: SortableTableHeaderProps = {
        headers: headerValues,
        setSortColumnAndDirection: (sortProperty, sortDirection) => {
            setCurrentPropertyByWhichToSort(sortProperty as SortOption);
            setCurrentSortDirection(sortDirection);
        },
        currentSort: currentPropertyByWhichToSort,
        currentSortDirection: currentSortDirection,
        tableIncludesCollapsibleRows: true,
        tableIncludesOverflowMenu: true,
    };

    return (
        <>
            <div className={styles.filtersContainer}>
                <div className={styles.filterByCreator}>
                    <FormFieldUserMultiSelect
                        selectedUsers={selectedCreatorIdsByWhichToFilter.map((userId) => cachedData.users.find((user) => user.cognito_subject === userId)).filter((user) => !!user)}
                        formFieldLabel="Filter by Creator"
                        formFieldId="filterByCreator"
                        onUsersSelected={(users) => {
                            setSelectedCreatorIdsByWhichToFilter((users ?? []).map((user) => user.cognito_subject));
                        }}
                    />
                </div>
                <div className={styles.filterByManager}>
                    <FormFieldUserMultiSelect
                        rolesRestriction={TPRM_ROLES}
                        formFieldLabel="Filter by Manager"
                        formFieldId="filterByManager"
                        onUsersSelected={(users) => {
                            setSelectedManagerIdsByWhichToFilter((users ?? []).map((user) => user.cognito_subject));
                        }}
                    />
                </div>
                <div className={styles.filterByStatus}>
                    <FormFieldMultiOptionSelect formFieldLabel="Filter by Status" formFieldId="filterByStatus" handleChange={(value: GroupOptionType[]) => setSelectedStatusesByWhichToFilter(value.map((tag) => tag.value as FinalizationState))} options={statusFilterOptions} accessibilityLabel="Select statuses by which to filter" />
                </div>
                <div className={styles.filterByTags}>
                    <FormFieldMultiOptionSelect formFieldLabel="Filter by Tags" formFieldId="filterByTags" handleChange={(value: GroupOptionType[]) => setSelectedTagIdsByWhichToFilter(value.map((tag) => tag.value as string))} options={cachedData.getTagCategoriesGroupedOptions()} accessibilityLabel="Select tags by which to filter" />
                </div>
            </div>
            <Table>
                <SortableTableHeader {...sortableTableProps} />
                <TableBody>
                    {dataForRows.map((data) => (
                        // Note on `columnSpan`: expandable rows span all columns except for the first one, with the expand buttons. +1 happens because of the overflow menu column.
                        <ThirdPartiesAndServicesTableRow key={data.serviceData?.service ? data.serviceData.service.id : data.thirdParty.id} data={data} columnSpan={headerValues.length + 1} {...props} />
                    ))}
                </TableBody>
            </Table>
        </>
    );
};
